import { Box, SxProps } from '@mui/material';
import React, { useRef } from 'react';
import { Header } from 'components/Header';
import { Paths } from 'services/navigation/paths';
import { BackButton } from 'components/Buttons';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { emailSchema, Form, Fields, FormRef } from 'common/form';
import { ApiPaths } from 'modules/calendar/constants/apiPaths';
import { usePostRest } from 'common/hooks/usePostRest';
import { FailedMessageWithHref } from 'common/ui';
import { ToastType, useToast } from 'services/toast';
import axios from 'axios';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  py: 5,
  px: 6
};

const validationSchemaWithConnect = Yup.object({
  name: Yup.string().required(`Add recipient name.`),
  email: emailSchema,
  reference: Yup.string().optional(),
  askToConnectCalendar: Yup.boolean().required(
    'Select connect calendar option.'
  )
});

const validationSchemaWithoutConnect = Yup.object({
  name: Yup.string().required(`Add recipient name.`),
  email: emailSchema,
  reference: Yup.string().optional()
});

type FormDataWithConnect = Yup.InferType<typeof validationSchemaWithConnect>;
type FormDataWithoutConnect = Yup.InferType<
  typeof validationSchemaWithoutConnect
>;

type FormData = FormDataWithConnect | FormDataWithoutConnect;

const fieldsWithoutConnect: Fields<FormDataWithoutConnect> = {
  name: {
    label: `Recipient's name`,
    type: 'text',
    containerSx: {
      maxWidth: 500
    }
  },
  email: {
    label: `Recipient's email`,
    subLabel: 'Calendar invites will be sent here.',
    type: 'text',
    containerSx: {
      maxWidth: 700
    }
  },
  reference: {
    label: 'Reference (Optional)',
    subLabel: 'For example their organisation or department name.',
    type: 'text',
    containerSx: {
      maxWidth: 500
    }
  }
};

const fieldsWithConnect: Fields<FormDataWithConnect> = {
  ...fieldsWithoutConnect,
  askToConnectCalendar: {
    type: 'radio',
    label: 'Ask to connect calendar',
    controlSx: { fontWeight: 600 },
    sx: { fontWeight: 600 },
    options: [
      {
        value: true,
        label: 'Yes'
      },
      {
        value: false,
        label: 'No'
      }
    ]
  }
};

export const CalendarAddContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const virtualCalendarUsersEnabled = useFlag(Flags.VIRTUAL_CALENDAR_USERS);

  const { mutateAsync: externalInviteMutation, isPending: isSubmittingInvite } =
    usePostRest<void, { email: string; name: string; reference?: string }>(
      ApiPaths.EXTERNAL_INVITE
    );

  const {
    mutateAsync: createVirtualCalendarMutation,
    isPending: isSubmittingVirtualCalendar
  } = usePostRest<
    void,
    {
      email: string;
      name: string;
      reference?: string;
    }
  >(ApiPaths.CREATE_VIRTUAL_CALENDAR);

  const formRef = useRef<FormRef<FormData>>(null);

  const handleFormSubmit = async (formData: FormData) => {
    const { name, reference, email } = formData;

    const askToConnectCalendar =
      'askToConnectCalendar' in formData
        ? (formData as FormDataWithConnect).askToConnectCalendar
        : false;

    try {
      const payload = { reference, email, name };

      if (!virtualCalendarUsersEnabled || askToConnectCalendar) {
        await externalInviteMutation(payload);
      } else {
        await createVirtualCalendarMutation(payload);
      }

      showToast({
        title: 'Contact invited',
        message: 'Invitation sent successfully.',
        type: ToastType.Success
      });
      navigate(Paths.HOME_CALENDAR_MEETINGS);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const isConflict = error.status === 409;
        showToast({
          title: isConflict ? 'Contact already exists' : 'Something went wrong',
          message: isConflict ? undefined : (
            <FailedMessageWithHref
              failedMessage="Try adding this contact again."
              emailSubject="Failed to add contact to calendar"
            />
          ),
          type: ToastType.Error
        });

        if (isConflict) {
          formRef?.current?.setValue('name', '');
          formRef?.current?.setValue('email', '');
          formRef?.current?.setValue('reference', '');
        }
      } else {
        showToast({
          title: 'Something went wrong',
          message: (
            <FailedMessageWithHref
              failedMessage="Try adding this contact again."
              emailSubject="Failed to add contact to calendar"
            />
          ),
          type: ToastType.Error
        });
      }
    }
  };

  return (
    <Box sx={container}>
      <Header
        title="Add contact"
        prevPagePath={Paths.HOME_CALENDAR_MEETINGS}
        prevPageTitle="MEETINGS"
        currentPageTitle="Add Contact"
        subtitle="Ask external stakeholders to link an Outlook or Google calendar to Popp, so you can add them to meetings."
      />
      <BackButton />
      <Box sx={{ mb: 5 }} />
      <Form
        ref={formRef}
        schema={
          virtualCalendarUsersEnabled
            ? validationSchemaWithConnect
            : validationSchemaWithoutConnect
        }
        fields={
          virtualCalendarUsersEnabled ? fieldsWithConnect : fieldsWithoutConnect
        }
        submitButtonProps={{
          label: 'Invite',
          loading: isSubmittingInvite || isSubmittingVirtualCalendar,
          disabled: isSubmittingInvite || isSubmittingVirtualCalendar,
          sx: {
            py: '16px'
          }
        }}
        cancelButtonProps={{
          variant: 'tertiary',
          label: 'Cancel',
          onClick: () => navigate(-1)
        }}
        onSubmit={handleFormSubmit}
      />
    </Box>
  );
};
